import { Environment } from "@core/config/environment";
import { Path } from "@core/routing/path";
import { contentStateFromString, contentStateToString } from "@utils/richText/converters";
import { Carousel } from "@domain/carousel/carousel";
import { carouselFromDto } from "@domain/carousel/dtos";
import { GroupDto } from "@domain/group/dtos";
import { LivecourseDto, livecourseFromDto } from "@domain/livecourse/dtos";
import { TextSection } from "@domain/lms/article/article";
import { ArticleDto, articleFromDto } from "@domain/lms/article/dtos";
import { CourseDto, courseFromDto } from "@domain/lms/course/dtos";
import { QuizDto, quizFromDto } from "@domain/lms/quiz/dto";
import { MediaDto, mediaFromDto } from "@domain/media/dtos";
import { OrganizationDto, organizationFromDto } from "@domain/organization/dtos";
import { PostDto } from "@domain/post/dtos";

export interface PostForOpenGraph {
	title: string;
	image: string;
	description: string;
	ogUrl: string;
}
export interface PostForOpenGraphToDto extends Omit<Partial<PostForOpenGraph>, "ogUrl"> {
	ogUrl?: string;
}

const TOOSHARE_DESCRIPTION =
	"TooShare - The first African Social Learning Plateform dedicated to online education and knowledge sharing";
const TOOSHARE_DEFAULT_IMAGE = `${Environment.APP_BASE_URL}/screenshot.10d75a82.png`;
const TOOSHARE_NAME = "TooShare";

function articleFromPostDto(dto: ArticleDto): PostForOpenGraphToDto {
	const article = articleFromDto(dto);

	const articleContent = article?.content?.sections
		?.filter((s): s is TextSection => s.type === "TEXT" && !!s.content)
		.map((s) => s.content.getPlainText())
		.join();

	return {
		title: article.title,
		image: article.banner?.url,
		description: articleContent,
		// ogUrl: `${Config.APP_BASE_URL}${Path.Article(article.id)}`,
	};
}

function courseFromPostDto(dto: CourseDto): PostForOpenGraphToDto {
	const course = courseFromDto(dto);

	return {
		title: course.title,
		image: course.banner?.url,
		description: contentStateFromString(contentStateToString(course.description ?? "") ?? "").getPlainText(),
		// ogUrl: `${Config.APP_BASE_URL}${Path.Lms.Course(course.id)}`,
	};
}
function groupFromPostDto(dto: GroupDto): PostForOpenGraphToDto {
	const banner = dto.banner ? mediaFromDto(dto.banner) : undefined;

	return {
		title: dto.name,
		image: banner?.url,
		description: dto.description,
		// ogUrl: `${Config.APP_BASE_URL}${dto.type === "SHAREROOM" ? Path.Shareroom(dto) : Path.Discussion(dto)}`,
	};
}

function organizationFromPostDto(dto: OrganizationDto): PostForOpenGraphToDto {
	const organization = organizationFromDto(dto);

	return {
		title: organization.primaryName,
		image: organization.banner?.url,
		description: organization.description,
		// ogUrl: `${Config.APP_BASE_URL}${Path.Organization(organization)}`,
	};
}
function quizFromPostDto(dto: QuizDto): PostForOpenGraphToDto {
	const quiz = quizFromDto(dto);

	return {
		title: quiz.title,
		description: contentStateFromString(contentStateToString(quiz.description ?? "") ?? "").getPlainText(),
		// ogUrl: `${Config.APP_BASE_URL}${Path.Quiz(quiz.id)}`,
	};
}

function LiveFromPostDto(dto: LivecourseDto): PostForOpenGraphToDto {
	const live = livecourseFromDto(dto);

	return {
		title: live.title,
		image: live.banner?.url,
		description: contentStateFromString(live.description ?? "").getPlainText(),
		// ogUrl: `${Config.APP_BASE_URL}${Path.LiveGroupInfos(live.id, live.shareroom.id)}`,
	};
}
function carouselFromPostDto(dto: Carousel): PostForOpenGraphToDto {
	const carousel = carouselFromDto(dto);

	return {
		title: carousel.title,
		description: "",
		// ogUrl: `${Config.APP_BASE_URL}${Path.Carousel(carousel.id)}`,
	};
}

function basicPostFromPostDto(post: PostDto): PostForOpenGraphToDto {
	const { urlPreview } = post;
	return {
		title: urlPreview?.title ?? post.author.primaryName,
		description: urlPreview?.description ?? contentStateFromString(post.text ?? "").getPlainText(),
		image: urlPreview?.image ?? getImageFromAssets(post.assets),
		// ogUrl: `${Config.APP_BASE_URL}${Path.Post(post.id)}`,
	};
}

function getImageFromAssets(assets: MediaDto[]): string | undefined {
	for (const asset of assets) {
		if (asset.mimeType.startsWith("image")) {
			return asset.url;
		}
		if (asset.mimeType.startsWith("video") && asset.metadata?.thumbnail) {
			return `${asset.url}${asset.metadata?.thumbnail}`;
		}
	}
	return undefined;
}

function postOpenGraphToDto(post: PostForOpenGraphToDto, postId: string): PostForOpenGraph {
	return {
		title: post.title && post.title.length !== 0 ? post.title : TOOSHARE_NAME,
		description: post.description && post.description.length !== 0 ? post.description : TOOSHARE_DESCRIPTION,
		image: post.image && post.image.length !== 0 ? post.image : TOOSHARE_DEFAULT_IMAGE,
		ogUrl: `${Environment.APP_BASE_URL}${Path.Post(postId)}`,
	};
}

export function postFromDtoForOpenGraph(dto: PostDto): PostForOpenGraph {
	switch (dto.type) {
		case "BASIC":
			return postOpenGraphToDto(basicPostFromPostDto(dto), dto.id);
		case "SHARE_ARTICLE":
			return postOpenGraphToDto(articleFromPostDto(dto.reference as ArticleDto), dto.id);
		case "SHARE_COURSE":
			return postOpenGraphToDto(courseFromPostDto(dto.reference as CourseDto), dto.id);
		case "SHARE_GROUP":
			return postOpenGraphToDto(groupFromPostDto(dto.reference as GroupDto), dto.id);
		case "SHARE_ORGANIZATION":
			return postOpenGraphToDto(organizationFromPostDto(dto.reference as OrganizationDto), dto.id);
		case "SHARE_QUIZ":
			return postOpenGraphToDto(quizFromPostDto(dto.reference as QuizDto), dto.id);
		case "SHARE_LIVE_COURSE":
			return postOpenGraphToDto(LiveFromPostDto(dto.reference as LivecourseDto), dto.id);
		case "SHARE_CAROUSEL":
			return postOpenGraphToDto(carouselFromPostDto(dto.reference as Carousel), dto.id);
		default:
			break;
	}

	return postOpenGraphToDto(basicPostFromPostDto(dto), dto.id);
}

export function defaultPost(): PostForOpenGraph {
	return {
		title: TOOSHARE_NAME,
		image: TOOSHARE_DEFAULT_IMAGE,
		description: TOOSHARE_DESCRIPTION,
		ogUrl: Environment.APP_BASE_URL as string,
	};
}
